import React, { useRef, useState, useEffect } from 'react';
import { Pencil, Highlighter, Eraser, Check, X, Minus, ArrowRight, Type } from 'lucide-react';
import { toast } from 'sonner';

const AnnotationCanvas = ({ backgroundImage, onSave, initialAnnotation }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('pen');
  const [ctx, setCtx] = useState(null);
  const [startPos, setStartPos] = useState(null);
  const [tempCanvas, setTempCanvas] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    setCtx(context);

    // Create temporary canvas for preview
    const temp = document.createElement('canvas');
    setTempCanvas(temp);

    // Load background image
    if (backgroundImage) {
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width || 800;
        canvas.height = img.height || 600;
        temp.width = canvas.width;
        temp.height = canvas.height;
        context.drawImage(img, 0, 0);
        
        // Load initial annotation if provided
        if (initialAnnotation) {
          const savedImg = new Image();
          savedImg.onload = () => {
            context.drawImage(savedImg, 0, 0);
          };
          savedImg.src = initialAnnotation;
        }
      };
      img.src = backgroundImage;
    }
  }, [backgroundImage, initialAnnotation]);

  const startDrawing = (e) => {
    if (!ctx) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate proper coordinates accounting for canvas scaling
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    
    setIsDrawing(true);
    setStartPos({ x, y });
    
    if (tool === 'tick') {
      drawTick(x, y);
      setIsDrawing(false);
    } else if (tool === 'cross') {
      drawCross(x, y);
      setIsDrawing(false);
    } else if (tool === 'text') {
      const text = prompt('Enter annotation text:');
      if (text && text.trim()) {
        ctx.font = '18px Inter, sans-serif';
        ctx.fillStyle = '#ef4444';
        ctx.textBaseline = 'top';
        ctx.fillText(text, x, y);
      }
      setIsDrawing(false);
    } else if (tool === 'line' || tool === 'arrow') {
      // Save current canvas state before drawing preview
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
      tempCtx.drawImage(canvas, 0, 0);
    } else if (tool !== 'line' && tool !== 'arrow') {
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
  };

  const draw = (e) => {
    if (!isDrawing || !ctx) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate proper coordinates accounting for canvas scaling
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    if (tool === 'pen') {
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineTo(x, y);
      ctx.stroke();
    } else if (tool === 'highlight') {
      ctx.strokeStyle = 'rgba(251, 191, 36, 0.5)';
      ctx.lineWidth = 20;
      ctx.lineCap = 'round';
      ctx.lineTo(x, y);
      ctx.stroke();
    } else if (tool === 'eraser') {
      ctx.clearRect(x - 10, y - 10, 20, 20);
    } else if (tool === 'line' || tool === 'arrow') {
      if (!tempCanvas || !startPos) return;
      
      // Clear and restore base image
      const img = new Image();
      img.src = tempCanvas.toDataURL();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        // Draw preview
        ctx.strokeStyle = '#ef4444';
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.fillStyle = '#ef4444';
        
        if (tool === 'line') {
          ctx.beginPath();
          ctx.moveTo(startPos.x, startPos.y);
          ctx.lineTo(x, y);
          ctx.stroke();
        } else if (tool === 'arrow') {
          drawArrow(ctx, startPos.x, startPos.y, x, y, '#ef4444', 3);
        }
      };
    }
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    
    if (ctx && (tool === 'line' || tool === 'arrow')) {
      // Save the final canvas state to temp
      const canvas = canvasRef.current;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
      tempCtx.drawImage(canvas, 0, 0);
    } else if (ctx) {
      ctx.closePath();
    }
  };

  const drawArrow = (context, fromX, fromY, toX, toY, strokeColor, strokeWidth) => {
    const headLength = 15 + strokeWidth * 2;
    const angle = Math.atan2(toY - fromY, toX - fromX);
    
    context.strokeStyle = strokeColor;
    context.fillStyle = strokeColor;
    context.lineWidth = strokeWidth;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    
    // Draw the main line
    context.beginPath();
    context.moveTo(fromX, fromY);
    context.lineTo(toX, toY);
    context.stroke();
    
    // Draw filled arrowhead
    context.beginPath();
    context.moveTo(toX, toY);
    context.lineTo(
      toX - headLength * Math.cos(angle - Math.PI / 7),
      toY - headLength * Math.sin(angle - Math.PI / 7)
    );
    context.lineTo(
      toX - headLength * Math.cos(angle + Math.PI / 7),
      toY - headLength * Math.sin(angle + Math.PI / 7)
    );
    context.closePath();
    context.fill();
  };

  const drawTick = (x, y) => {
    if (!ctx) return;
    ctx.strokeStyle = '#10b981';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x - 10, y);
    ctx.lineTo(x - 3, y + 10);
    ctx.lineTo(x + 10, y - 10);
    ctx.stroke();
  };

  const drawCross = (x, y) => {
    if (!ctx) return;
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x - 10, y - 10);
    ctx.lineTo(x + 10, y + 10);
    ctx.moveTo(x + 10, y - 10);
    ctx.lineTo(x - 10, y + 10);
    ctx.stroke();
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (canvas && onSave) {
      onSave(canvas.toDataURL());
      toast.success('Annotations saved successfully!', {
        duration: 2000,
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="space-y-2">
        {/* Tool Name Indicator */}
        <div className="bg-indigo-100 text-indigo-900 px-4 py-2 rounded-lg text-center font-semibold">
          Current Tool: {tool.toUpperCase()}
        </div>
        
        {/* Tool Buttons */}
        <div className="flex items-center gap-3 bg-white p-3 rounded-full shadow-lg border border-slate-200 flex-wrap">
        <button
          data-testid="annotation-tool-pen"
          onClick={() => {
            setTool('pen');
          }}
          className={`p-2 rounded-full transition-all ${
            tool === 'pen'
              ? 'bg-red-500 text-white ring-4 ring-red-300'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          title="Pen"
        >
          <Pencil className="w-4 h-4" />
        </button>
        
        <button
          data-testid="annotation-tool-line"
          onClick={() => {
            setTool('line');
          }}
          className={`p-2 rounded-full transition-all ${
            tool === 'line'
              ? 'bg-red-500 text-white ring-4 ring-red-300'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          title="Straight Line"
        >
          <Minus className="w-4 h-4" />
        </button>
        
        <button
          data-testid="annotation-tool-arrow"
          onClick={() => {
            setTool('arrow');
          }}
          className={`p-2 rounded-full transition-all ${
            tool === 'arrow'
              ? 'bg-red-500 text-white ring-4 ring-red-300'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          title="Arrow"
        >
          <ArrowRight className="w-4 h-4" />
        </button>
        
        <button
          data-testid="annotation-tool-highlight"
          onClick={() => {
            setTool('highlight');
          }}
          className={`p-2 rounded-full transition-all ${
            tool === 'highlight'
              ? 'bg-yellow-400 text-white ring-4 ring-yellow-300'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          title="Highlight"
        >
          <Highlighter className="w-4 h-4" />
        </button>
        
        <button
          data-testid="annotation-tool-text"
          onClick={() => {
            setTool('text');
          }}
          className={`p-2 rounded-full transition-all ${
            tool === 'text'
              ? 'bg-red-500 text-white ring-4 ring-red-300'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          title="Text Label"
        >
          <Type className="w-4 h-4" />
        </button>
        
        <button
          data-testid="annotation-tool-tick"
          onClick={() => {
            setTool('tick');
          }}
          className={`p-2 rounded-full transition-all ${
            tool === 'tick'
              ? 'bg-green-500 text-white ring-4 ring-green-300'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          title="Tick Mark"
        >
          <Check className="w-4 h-4" />
        </button>
        
        <button
          data-testid="annotation-tool-cross"
          onClick={() => {
            setTool('cross');
          }}
          className={`p-2 rounded-full transition-all ${
            tool === 'cross'
              ? 'bg-red-500 text-white ring-4 ring-red-300'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          title="Cross Mark"
        >
          <X className="w-4 h-4" />
        </button>
        
        <button
          data-testid="annotation-tool-eraser"
          onClick={() => {
            setTool('eraser');
          }}
          className={`p-2 rounded-full transition-all ${
            tool === 'eraser'
              ? 'bg-indigo-700 text-white ring-4 ring-indigo-300'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          title="Eraser"
        >
          <Eraser className="w-4 h-4" />
        </button>
        
        {onSave && (
          <button
            data-testid="annotation-save-btn"
            onClick={handleSave}
            className="ml-auto px-4 py-2 rounded-full bg-indigo-700 text-white hover:bg-indigo-800 transition-all font-medium"
          >
            Save
          </button>
        )}
      </div>
      </div>

      {/* Canvas */}
      <div className="bg-slate-50 rounded-xl border-2 border-slate-300 p-4 overflow-auto">
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          className={`max-w-full bg-white ${
            tool === 'text' ? 'cursor-text' : 
            tool === 'eraser' ? 'cursor-pointer' :
            tool === 'tick' || tool === 'cross' ? 'cursor-pointer' :
            'canvas-cursor'
          }`}
          style={{ 
            border: '2px solid #cbd5e1',
            touchAction: 'none'
          }}
          data-testid="annotation-canvas"
        />
      </div>
    </div>
  );
};

export default AnnotationCanvas;